import React, { useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'

import { doughnutChartColors } from '../doughnut-chart-colors'

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`

const LabelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    top: 50px;
    right: 0;
    position: absolute;
    max-width: 100px;
    hyphens: auto;
    text-align: left;

    @media screen and (max-width: 1095px) {
        position: relative;
        align-self: flex-start;
        max-width: 200px;
        hyphens: auto;
    }
`
const Dot = styled.div<{ color: string }>`
    height: 11px;
    width: 11px;
    margin: 1px 4px;
    background-color: ${({ color }) => color};
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
`

const Label = styled.span<{ color: string }>`
    font-size: 14px;
    display: flex;
    transition: 0.2s all ease;
    color: ${({ theme }) => theme.font.darkestColor};

    &.selected {
        ${Dot} {
            filter: opacity(70%);
        }
        text-decoration: underline;
    }

    &:hover {
        cursor: default;
    }
`

const Path = styled.path`
    stroke-dasharray: 1000;
    stroke-dashoffset: 1000;
    animation: dash 1.5s linear forwards;
    transition: 0.2s all ease;

    &:hover {
        filter: opacity(70%);
        cursor: pointer;
    }

    @keyframes dash {
        to {
            stroke-dashoffset: 0;
        }
    }
`

const Text = styled.span`
    position: absolute;
    font-size: 24px;
`

function Donut(cx: number, cy: number, radius: number, data: Array<{ chargePointName: string; percentage: number }>) {
    if (!data) return

    function arcradius(cx: number, cy: number, radius: number, degrees: number) {
        const radians = ((degrees - 90) * Math.PI) / 180.0
        return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) }
    }

    let total = 0
    const arr = []
    let beg = 0
    let end = 0
    let count = 0

    for (let i = 0; i < data.length; i++) total += data[i].percentage

    for (let i = 0; i < data.length; i++) {
        const item = data[i]

        const tmp: {
            d: string
            index: number
            value: number
            text: string
        } = {
            index: 0,
            value: 0,
            d: '',
            text: '',
        }

        let p = Math.floor(((item.percentage + 1) / total) * 100)
        count += p

        if (i === length - 1 && count < 100) p = p + (100 - count)

        end = beg + (360 / 100) * p
        tmp.index = i
        tmp.value = item.percentage

        const b = arcradius(cx, cy, radius, end)
        const e = arcradius(cx, cy, radius, beg)
        const la = end - beg <= 180 ? 0 : 1

        tmp.d = [
            'M',
            Math.floor(b.x),
            Math.floor(b.y),
            'A',
            radius,
            radius,
            0,
            la,
            0,
            i === 0 ? Math.floor(e.x) + 7 : Math.floor(e.x),
            Math.floor(e.y),
        ].join(' ')
        tmp.text = item.chargePointName
        arr.push(tmp)

        beg = end
    }

    return arr
}

export type DoughnutChartProps = {
    data: Array<{ chargePointName: string; percentage: number }>
    className?: ClassName
    style?: React.CSSProperties
}

export const DoughnutChart = React.memo(function DoughnutChart({
    className,
    style,
    data,
}: DoughnutChartProps): React.ReactElement {
    const centerX = 100
    const centerY = 100
    const radius = 70

    const [showInfo, setShowInfo] = useState<{ text: string; label: string }>({ text: '', label: '' })
    const arr = Donut(centerX, centerY, radius, data)

    return (
        <Wrapper className={cn(className)} style={style}>
            <Wrapper>
                <svg id="svg" width="200" height="200">
                    {arr?.map((p, i) => (
                        <g key={i}>
                            <Path
                                d={p.d}
                                stroke={doughnutChartColors[i]}
                                fill="none"
                                strokeWidth="25"
                                onMouseEnter={() => setShowInfo({ text: `${p.value}%`, label: p.text })}
                                onMouseLeave={() => setShowInfo({ text: '', label: '' })}
                                data-testid={'path-' + p.text}
                            ></Path>
                        </g>
                    ))}
                </svg>
                {showInfo.text.length > 1 && <Text data-testid={'percentage-text'}>{showInfo.text}</Text>}
            </Wrapper>
            <LabelContainer>
                {arr?.map((p, i) => (
                    <Label
                        key={p.text}
                        color={doughnutChartColors[i]}
                        className={cn({ selected: showInfo.label === p.text })}
                        data-testid={'label-' + p.text}
                    >
                        <Dot color={doughnutChartColors[i]}></Dot>
                        {p.text}
                    </Label>
                ))}
            </LabelContainer>
        </Wrapper>
    )
})
